import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Phone, MapPin, Camera, Loader2, Trash2, PenLine, Save, Play, CheckCircle2, Send, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { StatusBadge, Field } from "../../components/Shared";
import { AuthImage } from "../../components/AuthImage";
import { SignaturePad } from "../../components/SignaturePad";
import { api, fmtDate, formatApiError } from "../../lib/api";
import { cachedGet, queuedMessage, submit } from "../../lib/offline";
import { MOVE_STATUS, PHOTO_STAGES } from "../../lib/labels";

const Card = ({ title, children, testid }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">{title}</h2>
    {children}
  </section>
);

const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

function InfoCard({ move, client, onStatus }) {
  return (
    <section className="rounded-2xl bg-[#0b2c6b] p-5 text-white" data-testid="nomade-move-info">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-white/60">{fmtDate(move.date)} · {move.start_time} · {move.volume_m3} m³</p>
          <h1 className="mt-1 text-xl font-bold">{move.title || move.client_name}</h1>
        </div>
        <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold" data-testid="nomade-move-status">{MOVE_STATUS[move.status]?.label}</span>
      </div>
      <div className="mt-4 space-y-2 text-sm">
        {[["Départ", move.origin], ["Arrivée", move.destination]].map(([l, a]) => (
          <a key={l} href={mapsUrl(a)} target="_blank" rel="noreferrer" className="flex items-start gap-2 rounded-lg bg-white/10 p-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-teal-300" /><span><b>{l} :</b> {a || "—"}</span></a>
        ))}
        {client.phone && <a href={`tel:${client.phone}`} className="flex items-center gap-2 rounded-lg bg-white/10 p-2.5" data-testid="nomade-call-client"><Phone className="h-4 w-4 text-teal-300" />Appeler {move.client_name} · {client.phone}</a>}
        <p className="text-xs text-white/70">Équipe : {move.staff_names.join(", ") || "—"} · Véhicules : {move.vehicle_names.join(", ") || "—"}</p>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button data-testid="nomade-start-button" disabled={move.status === "en_cours"} onClick={() => onStatus("en_cours")} className="bg-white/15 hover:bg-white/25"><Play className="mr-2 h-4 w-4" />Démarrer</Button>
        <Button data-testid="nomade-finish-button" disabled={move.status === "termine"} onClick={() => onStatus("termine")} className="bg-teal-500 hover:bg-teal-400"><CheckCircle2 className="mr-2 h-4 w-4" />Terminer</Button>
      </div>
    </section>
  );
}

function PhotosCard({ moveId, photos, reload }) {
  const input = useRef(null);
  const [stage, setStage] = useState("avant");
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      let queued = false;
      for (const f of files) {
        const res = await submit({ url: `/field/moves/${moveId}/photos`, method: "post", form: { file: f, stage }, moveId });
        queued = queued || res.queued;
      }
      toast.success(queuedMessage({ queued }, `${files.length} photo(s) enregistrée(s)`));
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (p) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    await api.delete(`/field/files/${p.id}`);
    reload();
  };
  return (
    <Card title={`Photos (${photos.length})`} testid="nomade-photos">
      <div className="mb-3 flex flex-wrap gap-2">
        {Object.entries(PHOTO_STAGES).map(([k, l]) => (
          <button key={k} data-testid={`photo-stage-${k}`} onClick={() => setStage(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${stage === k ? "bg-[#0b2c6b] text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <input ref={input} type="file" accept="image/*" capture="environment" multiple hidden onChange={upload} data-testid="photo-file-input" />
      <Button data-testid="take-photo-button" disabled={busy} onClick={() => input.current.click()} className="w-full bg-[#1559d6] py-6 text-base hover:bg-[#0b2c6b]">
        {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Camera className="mr-2 h-5 w-5" />}Prendre une photo « {PHOTO_STAGES[stage]} »
      </Button>
      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative" data-testid={`photo-${p.id}`}>
              <AuthImage fileId={p.id} alt={p.stage} className="aspect-square w-full rounded-lg object-cover" />
              <span className={`absolute left-1 top-1 rounded px-1.5 py-0.5 text-[10px] font-bold text-white ${p.stage === "dommage" ? "bg-red-600" : "bg-black/60"}`}>{PHOTO_STAGES[p.stage]}</span>
              <button onClick={() => remove(p)} data-testid={`delete-photo-${p.id}`} className="absolute right-1 top-1 rounded bg-white/90 p-1"><Trash2 className="h-3 w-3 text-red-600" /></button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ReportCard({ moveId, report }) {
  const [form, setForm] = useState({ arrival_time: "", end_time: "", notes: "", incidents: "", client_satisfied: true });
  useEffect(() => {
    if (report) setForm((f) => ({ ...f, ...report }));
  }, [report]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try {
      const { arrival_time, end_time, notes, incidents, client_satisfied } = form;
      const res = await submit({ url: `/field/moves/${moveId}/report`, method: "put", json: { arrival_time, end_time, notes, incidents, client_satisfied }, moveId });
      toast.success(queuedMessage(res, "Compte rendu enregistré"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Card title="Compte rendu de déménagement" testid="nomade-report">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Arrivée sur site"><Input type="time" data-testid="report-arrival-input" value={form.arrival_time} onChange={set("arrival_time")} /></Field>
        <Field label="Fin de chantier"><Input type="time" data-testid="report-end-input" value={form.end_time} onChange={set("end_time")} /></Field>
      </div>
      <div className="mt-3 space-y-3">
        <Field label="Déroulement"><Textarea rows={3} data-testid="report-notes-input" value={form.notes} onChange={set("notes")} placeholder="Accès, démontage, déroulé du chantier…" /></Field>
        <Field label="Incidents / casse / réserves"><Textarea rows={2} data-testid="report-incidents-input" value={form.incidents} onChange={set("incidents")} placeholder="Aucun" /></Field>
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="report-satisfied-switch" checked={form.client_satisfied} onCheckedChange={set("client_satisfied")} />Client satisfait</label>
        {report?.updated_by && <p className="text-xs text-slate-400">Dernière mise à jour par {report.updated_by} le {fmtDate(report.updated_at)}</p>}
        <Button data-testid="report-save-button" onClick={save} className="w-full bg-[#0b2c6b]"><Save className="mr-2 h-4 w-4" />Enregistrer le compte rendu</Button>
      </div>
    </Card>
  );
}

function SignDialog({ kind, moveId, onClose, onSaved }) {
  const pad = useRef(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pad.current.isEmpty()) return toast.error("La signature est vide");
    setBusy(true);
    try {
      const res = await submit({ url: `/field/moves/${moveId}/signatures`, method: "post", json: { kind, signer_name: name, image_data: pad.current.toDataURL() }, moveId });
      toast.success(queuedMessage(res, "Signature enregistrée"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sign-dialog">
        <DialogHeader><DialogTitle>Signature {kind === "depart" ? "au chargement (départ)" : "à la livraison (arrivée)"}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{kind === "depart" ? "Le client confirme l'inventaire chargé et l'état des biens au départ." : "Le client confirme la bonne livraison de ses biens, avec ou sans réserves."}</p>
        <Field label="Nom du signataire"><Input data-testid="signer-name-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <SignaturePad ref={pad} />
        <DialogFooter><Button data-testid="sign-save-button" disabled={busy || name.trim().length < 2} onClick={save} className="bg-[#0b2c6b]">Valider la signature</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkDialog({ kind, moveId, defaultEmail, onClose, onSaved }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/field/moves/${moveId}/sign-request`, { kind, email });
      toast.success(`Lien de signature envoyé à ${email}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sign-link-dialog">
        <DialogHeader><DialogTitle>Signature à distance — {kind === "depart" ? "Lettre de voiture" : "PV de livraison"}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Le client reçoit un e-mail avec un lien personnel (valable 7 jours) pour consulter le document et signer depuis son téléphone.</p>
        <Field label="E-mail du client"><Input data-testid="sign-link-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <DialogFooter><Button data-testid="sign-link-send-button" disabled={busy || !email} onClick={send} className="bg-[#0b2c6b]">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Envoyer le lien</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignaturesCard({ moveId, signatures, requests, clientEmail, reload }) {
  const [kind, setKind] = useState(null);
  const [linkKind, setLinkKind] = useState(null);
  return (
    <Card title="Signatures client" testid="nomade-signatures">
      <div className="grid gap-3 sm:grid-cols-2">
        {[["depart", "Départ (chargement)"], ["arrivee", "Arrivée (livraison)"]].map(([k, l]) => {
          const s = signatures.find((x) => x.sign_kind === k);
          const pending = requests.find((r) => r.kind === k && r.status === "pending");
          return (
            <div key={k} className="rounded-xl border border-slate-200 p-3" data-testid={`signature-slot-${k}`}>
              <p className="text-xs font-semibold text-slate-500">{l}</p>
              {s ? (
                <>
                  <AuthImage fileId={s.id} alt={`Signature ${l}`} className="mt-2 h-20 w-full rounded bg-slate-50 object-contain" testid={`signature-img-${k}`} />
                  <p className="mt-1 text-xs text-slate-600">{s.signer_name} · {fmtDate(s.created_at)}{s.remote && " · à distance"}</p>
                  {s.reserves && <p className="mt-1 text-xs text-amber-700" data-testid={`signature-reserves-${k}`}>Réserves : {s.reserves}</p>}
                </>
              ) : <p className="mt-2 text-xs text-slate-400">Non signé</p>}
              <Button size="sm" variant="outline" data-testid={`sign-button-${k}`} className="mt-2 w-full" onClick={() => setKind(k)}><PenLine className="mr-2 h-4 w-4" />{s ? "Refaire signer" : "Faire signer sur place"}</Button>
              <Button size="sm" variant="ghost" data-testid={`sign-link-button-${k}`} className="mt-1 w-full text-[#1559d6]" onClick={() => setLinkKind(k)}><Send className="mr-2 h-4 w-4" />Signature à distance par lien</Button>
              {pending && <p className="mt-1 text-[11px] text-amber-700" data-testid={`sign-link-pending-${k}`}>Lien envoyé à {pending.email} — en attente</p>}
            </div>
          );
        })}
      </div>
      {kind && <SignDialog kind={kind} moveId={moveId} onClose={() => setKind(null)} onSaved={reload} />}
      {linkKind && <LinkDialog kind={linkKind} moveId={moveId} defaultEmail={clientEmail} onClose={() => setLinkKind(null)} onSaved={reload} />}
    </Card>
  );
}

const CHANNEL = { sent: "envoyé", no_email: "pas d'e-mail client", no_phone: "pas de mobile client", not_configured: "SMS non configuré" };
const channelText = (v) => CHANNEL[v] || v.replace(/^error: /, "échec — ");

function EnRouteCard({ moveId, notice, reload }) {
  const [busy, setBusy] = useState(false);
  const go = () => {
    if (!navigator.onLine) return toast.error("Connexion requise pour prévenir le client");
    if (!navigator.geolocation) return toast.error("GPS indisponible sur cet appareil");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (p) => {
      try {
        const r = await api.post(`/field/moves/${moveId}/en-route`, { lat: p.coords.latitude, lon: p.coords.longitude });
        toast.success(`Client prévenu : arrivée estimée ${r.data.eta.replace(":", "h")}`);
        reload();
      } catch (e) {
        toast.error(formatApiError(e));
      }
      setBusy(false);
    }, () => { setBusy(false); toast.error("Autorisez la localisation pour estimer l'heure d'arrivée"); }, { timeout: 10000, maximumAge: 60000 });
  };
  return (
    <Card title="Prévenir le client" testid="nomade-enroute-card">
      <Button data-testid="nomade-enroute-button" disabled={busy} onClick={go} className="w-full bg-[#1559d6] hover:bg-[#0b2c6b]">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Navigation className="mr-2 h-4 w-4" />}En route — envoyer l'heure d'arrivée
      </Button>
      {notice && (
        <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm" data-testid="nomade-enroute-notice">
          <p className="font-semibold text-slate-900">Arrivée estimée : <span data-testid="nomade-enroute-eta">{notice.eta.replace(":", "h")}</span> ({notice.duration_min} min de trajet)</p>
          <p className="text-xs text-slate-500">E-mail : {channelText(notice.email)} · SMS : {channelText(notice.sms)} · par {notice.by} à {new Date(notice.sent_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
        </div>
      )}
    </Card>
  );
}

export default function NomadeMove() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [offline, setOffline] = useState(false);
  const load = () => cachedGet(`/field/moves/${id}`).then((r) => { setData(r.data); setOffline(r.offline); }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setStatus = async (status) => {
    const res = await submit({ url: `/field/moves/${id}/status`, method: "put", json: { status }, moveId: id });
    toast.success(queuedMessage(res, `Statut : ${MOVE_STATUS[status].label}`));
    if (res.queued) setData((d) => ({ ...d, move: { ...d.move, status } }));
    else load();
  };

  if (!data) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-[#1559d6]" />;
  return (
    <div className="fade-up space-y-4">
      <Link to="/nomade" className="inline-flex items-center text-sm font-medium text-slate-600" data-testid="nomade-back-link"><ArrowLeft className="mr-1 h-4 w-4" />Planning</Link>
      {offline && <p className="rounded-xl bg-slate-800 p-3 text-xs text-white" data-testid="nomade-offline-data">Données enregistrées sur l'appareil (hors ligne). Photos et signatures ajoutées seront envoyées au retour du réseau.</p>}
      <InfoCard move={data.move} client={data.client} onStatus={setStatus} />
      <EnRouteCard moveId={id} notice={data.move.eta_notice} reload={load} />
      <PhotosCard moveId={id} photos={data.photos} reload={load} />
      <SignaturesCard moveId={id} signatures={data.signatures} requests={data.sign_requests || []} clientEmail={data.client.email} reload={load} />
      <ReportCard moveId={id} report={data.report} />
      <div className="hidden"><StatusBadge map={MOVE_STATUS} value={data.move.status} /></div>
    </div>
  );
}
